import { loadProjectConfig, getProjectId } from "./config.js";
import { loadWasm, createInstance, mountFiles, extractParams, runOpenSCAD } from "./wasm.js";
import { initViewer, loadModel } from "./viewer.js";
import { buildCustomizerUI, getParamValues } from "./customizer.js";
import { exportModel, setInstanceFactory } from "./export.js";

let config = null;
let params = null;
let currentVars = {};
let renderPromise = null;
let renderTimeout = null;
let initialInstance = null;

async function init() {
  const projectId = getProjectId();
  if (!projectId) {
    document.getElementById("status-text").textContent = "Error: No project ID in URL. Use ?id=<uuid>";
    return;
  }

  try {
    config = await loadProjectConfig(projectId);
    document.getElementById("project-title").textContent = config.title || "OpenSCAD Customizer";

    if (config.description) {
      const descEl = document.getElementById("description");
      const marked = await import("https://cdn.jsdelivr.net/npm/marked@15/+esm");
      descEl.innerHTML = marked.parse(config.description);
      descEl.classList.add("markdown");
    }

    if (config["auto-render"] === false) {
      document.getElementById("auto-render-toggle").checked = false;
    }
    if (config["preview-by-default"] === true) {
      document.getElementById("preview-toggle").checked = true;
    }

    await loadWasm();
    setInstanceFactory(createInstance);
    initialInstance = await createInstance();

    await mountFiles(initialInstance, config.files, `/customizer/projects/${projectId}`);

    try {
      params = extractParams(initialInstance, config.files[0]);
      buildCustomizerUI(params, handleParamChange);

      if (params.params && params.params.vars) {
        for (const [name, def] of Object.entries(params.params.vars)) {
          currentVars[name] = def.value;
        }
      }
    } catch (e) {
      console.warn("No customizer params found:", e);
    }

    const canvas = document.getElementById("viewer-canvas");
    initViewer(canvas);

    document.getElementById("viewer-placeholder").classList.add("hidden");

    await render();

    setupControls();

    document.getElementById("status-text").textContent = "Ready.";
  } catch (e) {
    console.error("Initialization failed:", e);
    document.getElementById("status-text").textContent = `Error: ${e.message}`;
  }
}

function handleParamChange(name, value) {
  currentVars[name] = value;
  scheduleRender();
}

function scheduleRender() {
  const autoRender = document.getElementById("auto-render-toggle").checked;
  if (!autoRender) return;

  if (renderTimeout) clearTimeout(renderTimeout);
  renderTimeout = setTimeout(() => render(), 300);
}

async function render(force = false) {
  const mainFile = config.files[0];
  const autoRender = document.getElementById("auto-render-toggle").checked;
  const isPreview = document.getElementById("preview-toggle").checked;

  if (!autoRender && !force) return;

  const statusText = document.getElementById("status-text");
  statusText.textContent = isPreview ? "Rendering preview..." : "Rendering...";

  if (renderPromise) {
    try { await renderPromise; } catch (e) {}
  }

  renderPromise = (async () => {
    try {
      const data = await runOpenSCAD(initialInstance, mainFile, currentVars, isPreview, "wrl");
      await loadModel(data);
      statusText.textContent = isPreview ? "Preview ready." : "Render ready.";
    } catch (e) {
      console.error("Render failed:", e);
      statusText.textContent = `Render error: ${e.message}`;
    }
  })();

  return renderPromise;
}

function setupControls() {
  document.getElementById("btn-render").addEventListener("click", () => {
    render();
  });

  document.getElementById("auto-render-toggle").addEventListener("change", () => {
    if (document.getElementById("auto-render-toggle").checked) {
      render();
    }
  });

  document.getElementById("preview-toggle").addEventListener("change", () => {
    render();
  });

  document.getElementById("btn-export").addEventListener("click", async () => {
    const mainFile = config.files[0];
    const format = document.getElementById("export-format").value;
    const isPreview = document.getElementById("preview-toggle").checked;

    try {
      await exportModel(initialInstance, mainFile, currentVars, format, isPreview);
    } catch (e) {
      console.error("Export failed:", e);
      document.getElementById("status-text").textContent = `Export error: ${e.message}`;
    }
  });

  document.addEventListener("keydown", (e) => {
    if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") return;
    if (e.key === "f5" || e.key === "F5") {
      e.preventDefault();
      document.getElementById("preview-toggle").checked = true;
      render(true);
    }
    if (e.key === "f6" || e.key === "F6") {
      e.preventDefault();
      document.getElementById("preview-toggle").checked = false;
      render(true);
    }
  });
}

init();